import { createSlice, Slice } from '@reduxjs/toolkit';
import { IReqStatus } from 'modules/shared/interfaces/requestStatus';
import { RootStateOrAny } from 'react-redux';
import { IAppConfig } from '../interfaces/appConfig';

export const APP_CONFIGURATION_KEY_IN_STORE = 'app-configuration';

export interface IAppConfigurationState {
	isLoading: boolean;
	appConfig: IAppConfig;
	updateLessonTypeConfig: {
		isLoading: boolean;
		status: IReqStatus;
	};
	updatePronounConfig: {
		isLoading: boolean;
		status: IReqStatus;
	};

	updateRegionConfig: {
		isLoading: boolean;
		status: IReqStatus;
	};

	updateAppPlatformVersionsConfig: {
		isLoading: boolean;
		status: IReqStatus;
	};
	updateNumberCombinationConfig: {
		isLoading: boolean;
		status: IReqStatus;
	};

	updateTimeCombinationConfig: {
		isLoading: boolean;
		status: IReqStatus;
	};
}

const initialState: IAppConfigurationState = {
	isLoading: false,
	appConfig: {
		lessonTypesConfigs: [],
		pronounsConfigs: [],
		regionsConfigs: [],
		appPlatformsVersions: [],
		numberCombinationsConfigs: [],
		timeCombinationsConfigs: [],
	},
	updateLessonTypeConfig: {
		isLoading: false,
		status: {
			success: false,
			error: false,
		},
	},
	updatePronounConfig: {
		isLoading: false,
		status: {
			success: false,
			error: false,
		},
	},
	updateRegionConfig: {
		isLoading: false,
		status: {
			success: false,
			error: false,
		},
	},
	updateAppPlatformVersionsConfig: {
		isLoading: false,
		status: {
			success: false,
			error: false,
		},
	},
	updateNumberCombinationConfig: {
		isLoading: false,
		status: {
			success: false,
			error: false,
		},
	},
	updateTimeCombinationConfig: {
		isLoading: false,
		status: {
			success: false,
			error: false,
		},
	},
};

const appConfigurationSlice: Slice = createSlice({
	name: APP_CONFIGURATION_KEY_IN_STORE,
	initialState,
	reducers: {
		getAppConfig: (state: IAppConfigurationState, action) => {
			state.isLoading = true;
		},
		getAppConfigSuccess: (state: IAppConfigurationState, action) => {
			state.isLoading = false;
			state.appConfig = action.payload;
		},
		getAppConfigFailure: (state: IAppConfigurationState) => {
			state.isLoading = false;
			state.appConfig = initialState.appConfig;
		},

		updateLessonTypeConfig: (state: IAppConfigurationState) => {
			state.updateLessonTypeConfig.isLoading = true;
		},
		updateLessonTypeConfigSuccess: (state: IAppConfigurationState, action) => {
			state.updateLessonTypeConfig = {
				isLoading: false,
				status: {
					success: true,
					error: false,
				},
			};
			if (state.appConfig) {
				state.appConfig.lessonTypesConfigs =
					state.appConfig.lessonTypesConfigs.map(item => {
						if (item.type === action.payload.type) {
							return { ...item, ...action.payload };
						}
						return item;
					});
			}
		},
		updateLessonTypeConfigFailure: (state: IAppConfigurationState) => {
			state.updateLessonTypeConfig = {
				isLoading: false,
				status: {
					success: false,
					error: true,
				},
			};
		},
		resetUpdateLessonTypeConfig: (state: IAppConfigurationState) => {
			state.updateLessonTypeConfig = {
				isLoading: false,
				status: {
					success: false,
					error: false,
				},
			};
		},

		updatePronounConfig: (state: IAppConfigurationState) => {
			state.updatePronounConfig.isLoading = true;
		},
		updatePronounConfigSuccess: (state: IAppConfigurationState, action) => {
			state.updatePronounConfig = {
				isLoading: false,
				status: {
					success: true,
					error: false,
				},
			};
			if (state.appConfig) {
				state.appConfig.pronounsConfigs = state.appConfig.pronounsConfigs.map(
					item => {
						if (item.pronoun === action.payload.pronoun) {
							return { ...item, ...action.payload };
						}
						return item;
					},
				);
			}
		},
		updatePronounConfigFailure: (state: IAppConfigurationState) => {
			state.updatePronounConfig = {
				isLoading: false,
				status: {
					success: false,
					error: true,
				},
			};
		},
		resetUpdatePronounConfig: (state: IAppConfigurationState) => {
			state.updatePronounConfig = {
				isLoading: false,
				status: {
					success: false,
					error: false,
				},
			};
		},

		updateRegionConfig: (state: IAppConfigurationState) => {
			state.updateRegionConfig.isLoading = true;
		},
		updateRegionConfigSuccess: (state: IAppConfigurationState, action) => {
			state.updateRegionConfig = {
				isLoading: false,
				status: {
					success: true,
					error: false,
				},
			};
			if (state.appConfig) {
				state.appConfig.regionsConfigs = state.appConfig.regionsConfigs.map(
					item => {
						if (item.region === action.payload.region) {
							return { ...item, ...action.payload };
						}
						return item;
					},
				);
			}
		},
		updateRegionConfigFailure: (state: IAppConfigurationState) => {
			state.updateRegionConfig = {
				isLoading: false,
				status: {
					success: false,
					error: true,
				},
			};
		},
		resetUpdateRegionConfig: (state: IAppConfigurationState) => {
			state.updateRegionConfig = {
				isLoading: false,
				status: {
					success: false,
					error: false,
				},
			};
		},

		updateAppPlatformVersionsConfig: (state: IAppConfigurationState) => {
			state.updateAppPlatformVersionsConfig.isLoading = true;
		},
		updateAppPlatformVersionsConfigSuccess: (
			state: IAppConfigurationState,
			action,
		) => {
			state.updateAppPlatformVersionsConfig = {
				isLoading: false,
				status: {
					success: true,
					error: false,
				},
			};
			if (state.appConfig) {
				state.appConfig.appPlatformsVersions =
					state.appConfig.appPlatformsVersions.map(item => {
						if (item.platform === action.payload.platform) {
							return { ...item, versions: action.payload };
						}
						return item;
					});
			}
		},
		updateAppPlatformVersionsConfigFailure: (state: IAppConfigurationState) => {
			state.updateAppPlatformVersionsConfig = {
				isLoading: false,
				status: {
					success: false,
					error: true,
				},
			};
		},
		resetUpdateAppPlatformVersionsConfig: (state: IAppConfigurationState) => {
			state.updateAppPlatformVersionsConfig = {
				isLoading: false,
				status: {
					success: false,
					error: false,
				},
			};
		},

		updateNumberCombinationConfig: (state: IAppConfigurationState) => {
			state.updateNumberCombinationConfig.isLoading = true;
		},
		updateNumberCombinationConfigSuccess: (
			state: IAppConfigurationState,
			action,
		) => {
			state.updateNumberCombinationConfig = {
				isLoading: false,
				status: {
					success: true,
					error: false,
				},
			};
			if (state.appConfig) {
				state.appConfig.numberCombinationsConfigs =
					state.appConfig.numberCombinationsConfigs.map(item => {
						if (item.nameKey === action.payload.nameKey) {
							return { ...item, ...action.payload };
						}
						return item;
					});
			}
		},
		updateNumberCombinationConfigFailure: (state: IAppConfigurationState) => {
			state.updateNumberCombinationConfig = {
				isLoading: false,
				status: {
					success: false,
					error: true,
				},
			};
		},
		resetUpdateNumberCombinationConfig: (state: IAppConfigurationState) => {
			state.updateNumberCombinationConfig = {
				isLoading: false,
				status: {
					success: false,
					error: false,
				},
			};
		},

		updateTimeCombinationConfig: (state: IAppConfigurationState) => {
			state.updateTimeCombinationConfig.isLoading = true;
		},
		updateTimeCombinationConfigSuccess: (
			state: IAppConfigurationState,
			action,
		) => {
			state.updateTimeCombinationConfig = {
				isLoading: false,
				status: {
					success: true,
					error: false,
				},
			};
			if (state.appConfig) {
				state.appConfig.timeCombinationsConfigs =
					state.appConfig.timeCombinationsConfigs.map(item => {
						if (item.nameKey === action.payload.nameKey) {
							return { ...item, ...action.payload };
						}
						return item;
					});
			}
		},
		updateTimeCombinationConfigFailure: (state: IAppConfigurationState) => {
			state.updateTimeCombinationConfig = {
				isLoading: false,
				status: {
					success: false,
					error: true,
				},
			};
		},
		resetUpdateTimeCombinationConfig: (state: IAppConfigurationState) => {
			state.updateTimeCombinationConfig = {
				isLoading: false,
				status: {
					success: false,
					error: false,
				},
			};
		},
	},
});

export const {
	getAppConfig,
	getAppConfigSuccess,
	getAppConfigFailure,

	updateLessonTypeConfig,
	updateLessonTypeConfigSuccess,
	updateLessonTypeConfigFailure,
	resetUpdateLessonTypeConfig,

	updatePronounConfig,
	updatePronounConfigSuccess,
	updatePronounConfigFailure,
	resetUpdatePronounConfig,

	updateRegionConfig,
	updateRegionConfigSuccess,
	updateRegionConfigFailure,
	resetUpdateRegionConfig,

	updateAppPlatformVersionsConfig,
	updateAppPlatformVersionsConfigSuccess,
	updateAppPlatformVersionsConfigFailure,
	resetUpdateAppPlatformVersionsConfig,

	updateNumberCombinationConfig,
	updateNumberCombinationConfigSuccess,
	updateNumberCombinationConfigFailure,
	resetUpdateNumberCombinationConfig,

	updateTimeCombinationConfig,
	updateTimeCombinationConfigSuccess,
	updateTimeCombinationConfigFailure,
	resetUpdateTimeCombinationConfig,
} = appConfigurationSlice.actions;

export const appConfigurationStateSelector = (
	state: RootStateOrAny,
): IAppConfigurationState => state[APP_CONFIGURATION_KEY_IN_STORE];

export default appConfigurationSlice;
